import type { ActionItem, Newsletter } from '@/types';
import { ACTION_ITEM_STATUS_LABELS, SECTION_LABELS, SECTION_ORDER, getEnabledSections } from '@/types';
import { markdownToSafeHtml } from '@/utils/markdown';
import { formatDate } from '@/utils/dateUtils';
import { downloadTextFile } from '@/utils/clipboard';
import { copyHtmlToClipboard } from '@/utils/clipboard';
import { LOGO_SVG_MARKUP } from '@/assets/logo';

export const DOCUMENT_STYLES = `
  body { font-family: 'Inter', -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e2233; max-width: 800px; margin: 0 auto; padding: 40px 24px 64px; line-height: 1.65; }
  .brand-header { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
  .brand-header .brand-name { font-size: 13px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #141b4d; }
  h1 { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 6px; color: #101530; }
  .meta { color: #6b7280; font-size: 14px; margin-bottom: 8px; }
  .accent-rule { height: 4px; width: 56px; background: linear-gradient(90deg, #2563eb, #14b8a6); border-radius: 4px; margin: 0 0 32px; }
  h2 { font-size: 17px; font-weight: 700; margin: 36px 0 14px; padding-left: 12px; border-left: 4px solid #2563eb; color: #141b4d; }
  h3 { font-size: 14px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; margin: 20px 0 8px; }
  p { margin: 0 0 12px; }
  ul, ol { margin: 0 0 12px; padding-left: 22px; }
  .kpi-grid { display: flex; flex-wrap: wrap; gap: 14px; margin: 8px 0 16px; }
  .kpi-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px 18px; min-width: 150px; background: #fafafe; }
  .kpi-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
  .kpi-value { font-size: 24px; font-weight: 800; color: #141b4d; margin-top: 2px; }
  .kpi-target { font-size: 12px; color: #9ca3af; margin-top: 2px; }
  table.action-items { width: 100%; border-collapse: collapse; margin: 8px 0 16px; font-size: 14px; }
  table.action-items th { text-align: left; padding: 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; border-bottom: 2px solid #e5e7eb; }
  table.action-items td { text-align: left; padding: 10px 8px; border-bottom: 1px solid #f0f1f5; }
  .status-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #eff6ff; color: #1d4ed8; }
  .status-completed { background: #ecfdf5; color: #047857; }
  .status-blocked { background: #fef2f2; color: #b91c1c; }
  @media print {
    body { padding: 0; }
    h2 { break-after: avoid; }
  }
`;

function renderBrandHeaderHtml(): string {
  return `<div class="brand-header">${LOGO_SVG_MARKUP('#141b4d', 26)}<span class="brand-name">PulseAI</span></div>`;
}

function renderKpisHtml(newsletter: Newsletter): string {
  if (newsletter.kpis.length === 0) return '';
  const cards = newsletter.kpis
    .map(
      (kpi) => `
      <div class="kpi-card">
        <div class="kpi-label">${escapeHtml(kpi.label)}</div>
        <div class="kpi-value">${escapeHtml(kpi.value)}</div>
        ${kpi.target ? `<div class="kpi-target">Target: ${escapeHtml(kpi.target)}</div>` : ''}
      </div>`,
    )
    .join('');
  return `<h2>Metrics &amp; KPIs</h2><div class="kpi-grid">${cards}</div>`;
}

function renderActionItemsHtml(actionItems: ActionItem[]): string {
  const open = actionItems.filter((item) => item.status !== 'completed');
  const completed = actionItems.filter((item) => item.status === 'completed');
  if (open.length === 0 && completed.length === 0) return '';

  const rows = (items: ActionItem[]) =>
    items
      .map(
        (item) => `
      <tr>
        <td>${escapeHtml(item.title)}</td>
        <td>${escapeHtml(item.owner)}</td>
        <td>${formatDate(item.dueDate)}</td>
        <td><span class="status-badge status-${item.status}">${ACTION_ITEM_STATUS_LABELS[item.status]}</span></td>
      </tr>`,
      )
      .join('');

  return `
    <h2>Action Items</h2>
    <table class="action-items">
      <thead><tr><th>Item</th><th>Owner</th><th>Due</th><th>Status</th></tr></thead>
      <tbody>${rows(open)}</tbody>
    </table>
    ${
      completed.length > 0
        ? `<h3>Completed</h3><table class="action-items"><tbody>${rows(completed)}</tbody></table>`
        : ''
    }`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Renders the inner body markup shared by the on-page preview and every export path, honoring each section's include/exclude toggle. */
export function buildNewsletterBodyHtml(newsletter: Newsletter, actionItems: ActionItem[]): string {
  const enabled = getEnabledSections(newsletter);
  const sectionsHtml = SECTION_ORDER.filter((key) => enabled.includes(key) && newsletter.sections[key]?.trim())
    .map(
      (key) => `<h2>${SECTION_LABELS[key]}</h2>${markdownToSafeHtml(newsletter.sections[key])}`,
    )
    .join('');

  return `
    ${renderBrandHeaderHtml()}
    <h1>${escapeHtml(newsletter.title)}</h1>
    <div class="meta">${formatDate(newsletter.periodStart)} – ${formatDate(newsletter.periodEnd)} · ${
      newsletter.status === 'published' ? 'Published' : 'Draft'
    }</div>
    <div class="accent-rule"></div>
    ${enabled.includes('kpis') ? renderKpisHtml(newsletter) : ''}
    ${sectionsHtml}
    ${enabled.includes('actionItems') ? renderActionItemsHtml(actionItems) : ''}
  `;
}

export function buildStandaloneHtmlDocument(newsletter: Newsletter, actionItems: ActionItem[]): string {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(newsletter.title)}</title>
<style>${DOCUMENT_STYLES}</style>
</head>
<body>
${buildNewsletterBodyHtml(newsletter, actionItems)}
</body>
</html>`;
}

export function exportNewsletterAsHtml(newsletter: Newsletter, actionItems: ActionItem[]): void {
  const html = buildStandaloneHtmlDocument(newsletter, actionItems);
  downloadTextFile(`${slugify(newsletter.title)}.html`, html, 'text/html');
}

/** Pragmatic PDF/print path: the browser's native print-to-PDF dialog, triggered on the print-styled preview. */
export function printNewsletter(): void {
  window.print();
}

export async function copyNewsletterToClipboard(newsletter: Newsletter, actionItems: ActionItem[]): Promise<void> {
  const html = buildNewsletterBodyHtml(newsletter, actionItems);
  const enabled = getEnabledSections(newsletter);
  const plainText = SECTION_ORDER.filter((key) => enabled.includes(key) && newsletter.sections[key]?.trim())
    .map((key) => `${SECTION_LABELS[key]}\n${newsletter.sections[key]}`)
    .join('\n\n');
  await copyHtmlToClipboard(html, `${newsletter.title}\n\n${plainText}`);
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
